import { useEffect, useState } from "react";
import { api, setAdminToken } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Plus, Trash2, Copy, Save, X, ChevronDown, ChevronUp, Edit3,
  ClipboardCheck, GripVertical,
} from "lucide-react";
import { toast } from "sonner";

const emptyTemplate = () => ({
  name: "New Checklist",
  description: "",
  active: true,
  service_hints: [],
  sections: [{ title: "Section 1", items: [{ key: `item_${Date.now()}`, label: "New item" }] }],
});

const AdminChecklistTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template object being edited (or draft)
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/checklist-templates");
      setTemplates(res.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
      } else {
        toast.error("Failed to load templates");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const startCreate = () => setEditing({ ...emptyTemplate(), _new: true });
  const startEdit = (tpl) => setEditing(JSON.parse(JSON.stringify(tpl)));
  const cancelEdit = () => setEditing(null);

  const duplicate = async (id) => {
    try {
      await api.post(`/admin/checklist-templates/${id}/duplicate`);
      toast.success("Duplicated");
      load();
    } catch { toast.error("Duplicate failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? Jobs already using it keep their snapshot.")) return;
    try {
      await api.delete(`/admin/checklist-templates/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (tpl) => {
    try {
      await api.put(`/admin/checklist-templates/${tpl.id}`, { active: !tpl.active });
      load();
    } catch { toast.error("Update failed"); }
  };

  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Name required"); return; }
    // Auto-generate item keys where missing
    const cleaned = {
      name: editing.name.trim(),
      description: editing.description || "",
      active: editing.active !== false,
      service_hints: (editing.service_hints || []).map((h) => (h || "").trim()).filter(Boolean),
      sections: (editing.sections || []).map((s) => ({
        title: (s.title || "Section").trim(),
        items: (s.items || []).map((it, idx) => ({
          key: (it.key || "").trim() || `k_${Date.now()}_${idx}`,
          label: (it.label || "").trim() || "Item",
        })),
      })),
    };
    setSaving(true);
    try {
      if (editing._new) {
        await api.post("/admin/checklist-templates", cleaned);
        toast.success("Template created");
      } else {
        await api.put(`/admin/checklist-templates/${editing.id}`, cleaned);
        toast.success("Template saved");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      eyebrow="Workshop OS"
      title="Checklist Templates"
      actions={
        <button onClick={startCreate} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-spark-yellow-dark" data-testid="new-template-btn">
          <Plus className="size-3.5" /> New template
        </button>
      }
    >
      {loading ? (
        <div className="text-center py-20 text-slate-400"><Loader2 className="size-6 animate-spin inline" /></div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] py-14 text-center max-w-xl mx-auto">
          <ClipboardCheck className="size-10 text-spark-yellow mx-auto mb-3" />
          <p className="font-display uppercase tracking-wider text-slate-200">No templates yet</p>
          <p className="text-sm text-slate-400 mt-1">Create your first checklist template — technicians can attach it to any job.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="templates-list">
          {templates.map((tpl) => {
            const totalItems = (tpl.sections || []).reduce((n, s) => n + (s.items?.length || 0), 0);
            return (
              <li key={tpl.id} className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`template-row-${tpl.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display uppercase tracking-tight text-lg font-bold text-white truncate">{tpl.name}</h3>
                    {tpl.active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] uppercase tracking-wider font-bold">Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-slate-400 text-[10px] uppercase tracking-wider font-bold">Archived</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-0.5 line-clamp-2">{tpl.description || "—"}</p>
                  <p className="text-[11px] text-slate-500 mt-1.5 font-mono">
                    {(tpl.sections || []).length} sections · {totalItems} items · slug: {tpl.slug}
                  </p>
                  {(tpl.service_hints || []).length > 0 && (
                    <p className="text-[11px] text-spark-yellow mt-1">Auto-suggests when quote mentions: {tpl.service_hints.join(", ")}</p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap">
                  <button onClick={() => toggleActive(tpl)} className="px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-xs uppercase tracking-wider font-bold text-slate-200" data-testid={`toggle-active-${tpl.slug}`}>
                    {tpl.active ? "Archive" : "Activate"}
                  </button>
                  <button onClick={() => startEdit(tpl)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-xs uppercase tracking-wider font-bold text-slate-200" data-testid={`edit-${tpl.slug}`}>
                    <Edit3 className="size-3.5" /> Edit
                  </button>
                  <button onClick={() => duplicate(tpl.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-xs uppercase tracking-wider font-bold text-slate-200" data-testid={`duplicate-${tpl.slug}`}>
                    <Copy className="size-3.5" /> Duplicate
                  </button>
                  <button onClick={() => remove(tpl.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-xs uppercase tracking-wider font-bold text-red-400" data-testid={`delete-${tpl.slug}`}>
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <TemplateEditor
          value={editing}
          onChange={setEditing}
          onCancel={cancelEdit}
          onSave={save}
          saving={saving}
        />
      )}
    </AdminLayout>
  );
};

const TemplateEditor = ({ value, onChange, onCancel, onSave, saving }) => {
  const [collapsed, setCollapsed] = useState({});

  const setField = (k, v) => onChange({ ...value, [k]: v });

  const addSection = () => {
    onChange({
      ...value,
      sections: [...(value.sections || []), { title: `Section ${value.sections.length + 1}`, items: [{ key: `k_${Date.now()}`, label: "New item" }] }],
    });
  };

  const updateSection = (idx, patch) => {
    const next = [...value.sections];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...value, sections: next });
  };

  const removeSection = (idx) => {
    if (!window.confirm("Remove this section?")) return;
    onChange({ ...value, sections: value.sections.filter((_, i) => i !== idx) });
  };

  const moveSection = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= value.sections.length) return;
    const next = [...value.sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange({ ...value, sections: next });
  };

  const addItem = (secIdx) => {
    const next = [...value.sections];
    next[secIdx] = {
      ...next[secIdx],
      items: [...(next[secIdx].items || []), { key: `k_${Date.now()}`, label: "New item" }],
    };
    onChange({ ...value, sections: next });
  };

  const updateItem = (secIdx, itemIdx, patch) => {
    const next = [...value.sections];
    const items = [...next[secIdx].items];
    items[itemIdx] = { ...items[itemIdx], ...patch };
    next[secIdx] = { ...next[secIdx], items };
    onChange({ ...value, sections: next });
  };

  const removeItem = (secIdx, itemIdx) => {
    const next = [...value.sections];
    next[secIdx] = { ...next[secIdx], items: next[secIdx].items.filter((_, i) => i !== itemIdx) };
    onChange({ ...value, sections: next });
  };

  const moveItem = (secIdx, itemIdx, dir) => {
    const next = [...value.sections];
    const items = [...next[secIdx].items];
    const target = itemIdx + dir;
    if (target < 0 || target >= items.length) return;
    [items[itemIdx], items[target]] = [items[target], items[itemIdx]];
    next[secIdx] = { ...next[secIdx], items };
    onChange({ ...value, sections: next });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" role="dialog" aria-modal="true" data-testid="template-editor-modal">
      <div className="w-full max-w-3xl my-8 rounded-xl border border-white/10 bg-spark-ink shadow-2xl overflow-hidden">
        <div className="bg-spark-teal-deep px-6 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="label-mono text-spark-yellow">{value._new ? "New template" : "Edit template"}</p>
            <h2 className="font-display uppercase tracking-tight text-lg font-bold text-white">{value.name || "Untitled"}</h2>
          </div>
          <button onClick={onCancel} className="text-slate-300 hover:text-white p-1" data-testid="template-editor-close"><X className="size-5" /></button>
        </div>
        <div className="p-6 space-y-5 text-slate-100">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">Name</label>
              <input type="text" value={value.name} onChange={(e) => setField("name", e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500" data-testid="template-name-input" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">Service hints (comma-separated)</label>
              <input
                type="text"
                value={(value.service_hints || []).join(", ")}
                onChange={(e) => setField("service_hints", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="e.g. complete service, full service"
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500"
                data-testid="template-hints-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">Description</label>
            <textarea rows={2} value={value.description || ""} onChange={(e) => setField("description", e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 resize-none" data-testid="template-desc-input" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={value.active !== false} onChange={(e) => setField("active", e.target.checked)} className="size-4 accent-spark-teal" data-testid="template-active-toggle" />
            Active (available for technicians to attach)
          </label>

          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center justify-between mb-3">
              <p className="label-mono text-spark-yellow">Sections</p>
              <button onClick={addSection} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-spark-yellow-dark" data-testid="add-section-btn"><Plus className="size-3.5" /> Add section</button>
            </div>
            <ul className="space-y-3">
              {(value.sections || []).map((sec, si) => (
                <li key={si} className="rounded-md border border-white/10 bg-white/5 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-white/5 border-b border-white/10">
                    <button onClick={() => setCollapsed((s) => ({ ...s, [si]: !s[si] }))} className="text-slate-400 hover:text-white">
                      {collapsed[si] ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
                    </button>
                    <input
                      type="text"
                      value={sec.title}
                      onChange={(e) => updateSection(si, { title: e.target.value })}
                      className="flex-1 bg-transparent border-none focus:outline-none text-sm font-display uppercase tracking-tight font-bold text-white"
                      data-testid={`section-title-${si}`}
                    />
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => moveSection(si, -1)} disabled={si === 0} className="p-1 text-slate-400 hover:text-white disabled:opacity-30" title="Move up"><ChevronUp className="size-3.5" /></button>
                      <button onClick={() => moveSection(si, 1)} disabled={si === value.sections.length - 1} className="p-1 text-slate-400 hover:text-white disabled:opacity-30" title="Move down"><ChevronDown className="size-3.5" /></button>
                      <button onClick={() => removeSection(si)} className="p-1 text-red-400 hover:text-red-300" title="Remove section" data-testid={`remove-section-${si}`}><Trash2 className="size-3.5" /></button>
                    </div>
                  </div>
                  {!collapsed[si] && (
                    <div className="p-3 space-y-1.5">
                      {(sec.items || []).map((it, ii) => (
                        <div key={ii} className="flex items-center gap-2" data-testid={`section-${si}-item-${ii}`}>
                          <GripVertical className="size-3.5 text-slate-500 shrink-0" />
                          <input
                            type="text"
                            value={it.label}
                            onChange={(e) => updateItem(si, ii, { label: e.target.value })}
                            className="flex-1 bg-white/5 border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white"
                            placeholder="Item label"
                            data-testid={`item-label-${si}-${ii}`}
                          />
                          <button onClick={() => moveItem(si, ii, -1)} disabled={ii === 0} className="p-1 text-slate-400 hover:text-white disabled:opacity-30"><ChevronUp className="size-3.5" /></button>
                          <button onClick={() => moveItem(si, ii, 1)} disabled={ii === sec.items.length - 1} className="p-1 text-slate-400 hover:text-white disabled:opacity-30"><ChevronDown className="size-3.5" /></button>
                          <button onClick={() => removeItem(si, ii)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="size-3.5" /></button>
                        </div>
                      ))}
                      <button onClick={() => addItem(si)} className="inline-flex items-center gap-1 mt-1 text-xs text-spark-yellow hover:text-white" data-testid={`add-item-${si}`}>
                        <Plus className="size-3" /> Add item
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={saving} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5" data-testid="template-cancel">Cancel</button>
          <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-spark-yellow-dark disabled:opacity-60" data-testid="template-save">
            {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {saving ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminChecklistTemplates;
